import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Table,
  Tag,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Statistic,
  Typography,
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import moment from "moment";
import { getSalesOrderReports } from "../../../redux/actions/sale/salesOrderAction";
import { loadAllCustomersForSearch } from "../../../redux/actions/customer/getCustomerAction";
import { CACHE_KEYS, getCachedDateFilter, setCachedDateFilter } from "../../../utils/dateFilterCache";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const SalesOrderReports = () => {
  const dispatch = useDispatch();
  const [reportType, setReportType] = useState("pending-items");
  const [filters, setFilters] = useState(() => {
    const c = getCachedDateFilter(CACHE_KEYS.SALES_ORDER_REPORTS);
    return {
      customer_id: "",
      start_date: c?.startdate ?? "",
      end_date: c?.enddate ?? "",
    };
  });

  const reports = useSelector((state) => state.salesOrders.reports);
  const allCustomers = useSelector((state) => state.customers.searchList || state.customers.list) || [];

  useEffect(() => {
    dispatch(loadAllCustomersForSearch({ status: true }));
  }, [dispatch]);

  const loadReport = useCallback(() => {
    const reportFilters = {};
    if (filters.customer_id) reportFilters.customer_id = filters.customer_id;
    if (filters.start_date) reportFilters.start_date = filters.start_date;
    if (filters.end_date) reportFilters.end_date = filters.end_date;
    dispatch(getSalesOrderReports(reportType, reportFilters));
  }, [dispatch, reportType, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const start_date = dates[0].format("YYYY-MM-DD");
      const end_date = dates[1].format("YYYY-MM-DD");
      setCachedDateFilter(CACHE_KEYS.SALES_ORDER_REPORTS, { startdate: start_date, enddate: end_date });
      setFilters((prev) => ({ ...prev, start_date, end_date }));
    } else {
      setFilters((prev) => ({ ...prev, start_date: "", end_date: "" }));
    }
  };

  const pendingItemsColumns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
    },
    {
      title: "Order Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      render: (customer) => customer?.name || "-",
    },
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (product) => product?.name || "-",
    },
    {
      title: "Ordered",
      dataIndex: "ordered_quantity",
      key: "ordered_quantity",
    },
    {
      title: "Fulfilled",
      dataIndex: "fulfilled_quantity",
      key: "fulfilled_quantity",
    },
    {
      title: "Pending",
      dataIndex: "pending_quantity",
      key: "pending_quantity",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "fulfilled" ? "green" : status === "partial" ? "blue" : "orange"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
  ];

  const completionColumns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
    },
    {
      title: "Order Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      render: (customer) => customer?.name || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "fulfilled" ? "green" : status === "partial" ? "blue" : "orange"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: "Ordered Qty",
      dataIndex: "total_ordered",
      key: "total_ordered",
    },
    {
      title: "Fulfilled Qty",
      dataIndex: "total_fulfilled",
      key: "total_fulfilled",
    },
    {
      title: "Completion %",
      dataIndex: "completion_percentage",
      key: "completion_percentage",
      render: (percentage) => `${percentage}%`,
    },
    {
      title: "Pending Qty",
      dataIndex: "pending_quantity",
      key: "pending_quantity",
    },
  ];

  const renderReport = () => {
    const reportData = reports?.[reportType];

    if (reportType === "pending-items") {
      return (
        <Table
          columns={pendingItemsColumns}
          dataSource={reportData?.pendingItems || []}
          rowKey={(record, index) => `${record.order_id}-${record.product?.id}-${index}`}
          pagination={{ pageSize: 20 }}
        />
      );
    }

    if (reportType === "completion") {
      return (
        <Table
          columns={completionColumns}
          dataSource={reportData?.completionReport || []}
          rowKey="order_id"
          pagination={{ pageSize: 20 }}
        />
      );
    }

    if (reportType === "status-summary") {
      const summary = reportData?.summary || {};
      return (
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending"
                value={summary.pending || 0}
                prefix={<Tag color="orange">P</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Partial"
                value={summary.partial || 0}
                prefix={<Tag color="blue">P</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Fulfilled"
                value={summary.fulfilled || 0}
                prefix={<Tag color="green">F</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Cancelled"
                value={summary.cancelled || 0}
                prefix={<Tag color="red">C</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} md={24} style={{ marginTop: "16px" }}>
            <Card>
              <Statistic
                title="Total Orders"
                value={summary.total || 0}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
        </Row>
      );
    }

    return null;
  };

  return (
    <div style={{ padding: "20px" }}>
      <Card>
        <Title level={3}>Sales Order Reports</Title>

        <Space style={{ marginBottom: "20px", flexWrap: "wrap" }}>
          <Select
            value={reportType}
            onChange={setReportType}
            style={{ width: 200 }}
          >
            <Option value="pending-items">Pending Items</Option>
            <Option value="completion">Completion Report</Option>
            <Option value="status-summary">Status Summary</Option>
          </Select>

          <Select
            placeholder="Filter by Customer"
            style={{ width: 200 }}
            allowClear
            value={filters.customer_id || undefined}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, customer_id: value || "" }))
            }
          >
            {allCustomers.map((customer) => (
              <Option key={customer.id} value={customer.id}>
                {customer.name}
              </Option>
            ))}
          </Select>

          <RangePicker
            onChange={handleDateRangeChange}
            format="DD-MM-YYYY"
          />

          <Button onClick={loadReport}>Refresh</Button>
        </Space>

        {renderReport()}
      </Card>
    </div>
  );
};

export default SalesOrderReports;
